import type { User } from "oidc-client-ts";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
} from "react";
import { useAuth as useOidcAuth } from "react-oidc-context";
import type { AccountInfo } from "@/api/models";
import { deletePersistantState } from "@/hooks/usePersistantState";
import { ActiveSelskapStorageKey } from "../selskap/SelskapContext";
import { OidcProvider } from "./OidcProvider";
import { useAccountState } from "./state";

export type AuthContext = {
  setupAccount: (account: AccountInfo) => Promise<void>;
  resetAccount: () => Promise<void>;
  account: AccountInfo | null;
  isAuthenticated: boolean;
  signinRedirect: () => Promise<void>;
  signoutRedirect: () => Promise<void>;
  oidcUser: User | null;
};

const AuthContext = createContext<AuthContext | null>(null);

export function AuthProvider({ children }: PropsWithChildren<unknown>) {
  return (
    <OidcProvider>
      <ApiAuthProvider>{children}</ApiAuthProvider>
    </OidcProvider>
  );
}

const clearState = () => {
  deletePersistantState(ActiveSelskapStorageKey, localStorage);
};

function ApiAuthProvider({ children }: PropsWithChildren<unknown>) {
  const oidcUser = useOidcAuth();

  const [account, setupAccountState] = useAccountState();
  const isAuthenticated = oidcUser.isAuthenticated && Boolean(account);

  const setupAccount = useCallback(
    async (account: AccountInfo) => {
      clearState();
      setupAccountState(account);
    },
    [setupAccountState],
  );

  const resetAccount = useCallback(async () => {
    setupAccountState(null);
    clearState();
  }, [setupAccountState]);

  if (oidcUser.isLoading) return null;

  return (
    <AuthContext.Provider
      value={
        {
          account,
          isAuthenticated,
          setupAccount,
          resetAccount,
          oidcUser: oidcUser.user,
          signinRedirect: oidcUser.signinRedirect,
          signoutRedirect: oidcUser.signoutRedirect,
        } as AuthContext
      }
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
